import React from 'react';
import { Card, Typography, Row, Col, Skeleton } from 'antd';
import { BaselineData, BaselineChanges } from '../../services/baselineService';
import {
  formatCurrency,
  formatPercent,
  formatCurrencyChange,
  formatPercentWithSign,
} from '../../utils/formatters';
import { BaselineDelta } from './BaselineDelta';

const { Text } = Typography;

interface BaselineCardProps {
  baseline: BaselineData | null;
  baselineDateRange: string;
  baselineChanges: BaselineChanges | null;
  loading: boolean;
  changeLoading: boolean;
}

export const BaselineCard: React.FC<BaselineCardProps> = ({
  baseline,
  baselineDateRange,
  baselineChanges,
  loading,
  changeLoading,
}) => {
  if (!baseline && !loading) {
    return null;
  }

  const renderValue = (value: React.ReactNode) =>
    loading ? <Skeleton.Input active size="small" style={{ width: '80%', maxWidth: 110 }} /> : value;

  const renderChange = (
    value: number | null | undefined,
    formatter: (val: number) => string,
    invertColor = false
  ) => {
    if (loading) {
      return null;
    }
    if (changeLoading) {
      return <Skeleton.Input active size="small" style={{ width: 60, marginTop: 4 }} />;
    }
    return <BaselineDelta changeValue={value ?? null} formatter={formatter} invertColor={invertColor} />;
  };

  // Use the pre-calculated 8-week GFS percent if available (calculated from averaging individual percentages)
  // Otherwise fall back to calculating from totals (for backwards compatibility)
  const eightWeekPercent =
    baseline?.eightWeekGfsPercent !== undefined && baseline?.eightWeekGfsPercent !== null
      ? baseline.eightWeekGfsPercent
      : baseline &&
        baseline.eightWeekTotalSales > 0 &&
        baseline.eightWeekTotalGfs !== null &&
        baseline.eightWeekTotalGfs !== undefined
      ? (baseline.eightWeekTotalGfs / baseline.eightWeekTotalSales) * 100
      : null;

  return (
    <Card size="small" style={{ marginTop: 8, backgroundColor: '#f5f5f5', border: '2px solid #d9d9d9' }}>
      <div style={{ marginBottom: 8 }}>
        <Text strong style={{ fontSize: 16, display: 'block' }}>
          Baseline Avg.
        </Text>
        {loading ? (
          <Skeleton.Input active size="small" style={{ width: 180, height: 16 }} />
        ) : (
          baselineDateRange && (
            <Text style={{ fontSize: 14, display: 'block', color: '#666' }}>({baselineDateRange})</Text>
          )
        )}
      </div>
      <Row gutter={[8, 8]}>
        <Col span={12}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Weekly Toast
          </Text>
          <div style={{ fontWeight: 500 }}>{renderValue(baseline ? formatCurrency(baseline.weeklyToast) : '--')}</div>
        </Col>
        <Col span={12}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Weekly Catering
          </Text>
          <div style={{ fontWeight: 500 }}>{renderValue(baseline ? formatCurrency(baseline.weeklyCatering) : '--')}</div>
        </Col>
        <Col span={12}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Weekly Total
          </Text>
          <div style={{ fontWeight: 500 }}>
            {renderValue(baseline ? formatCurrency(baseline.weeklyTotal) : '--')}
          </div>
          {renderChange(baselineChanges?.weeklyTotalDelta ?? null, formatCurrencyChange)}
        </Col>
        <Col span={12}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Weekly GFS
          </Text>
          <div style={{ fontWeight: 500 }}>
            {renderValue(
              baseline && baseline.weeklyGfs !== null && baseline.weeklyGfs > 0
                ? formatCurrency(baseline.weeklyGfs)
                : '--'
            )}
          </div>
          {renderChange(baselineChanges?.weeklyGfsDelta ?? null, formatCurrencyChange)}
        </Col>
        <Col span={12}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            GFS %
          </Text>
          <div style={{ fontWeight: 500 }}>
            {renderValue(baseline ? formatPercent(baseline.gfsPercent) : '--')}
          </div>
          {renderChange(baselineChanges?.gfsPercentPoints ?? null, formatPercentWithSign, true)}
        </Col>
        <Col span={12}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Variance from Avg
          </Text>
          <div style={{ fontWeight: 500 }}>
            {renderValue(baseline ? formatPercent(baseline.varianceFromAvg) : '--')}
          </div>
        </Col>
        <Col span={12}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            8th Week Sales
          </Text>
          <div style={{ fontWeight: 500 }}>
            {renderValue(
              baseline && baseline.eightWeekTotalSales > 0 ? formatCurrency(baseline.eightWeekTotalSales) : '--'
            )}
          </div>
        </Col>
        <Col span={12}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            8th Week GFS
          </Text>
          <div style={{ fontWeight: 500 }}>
            {renderValue(
              baseline && baseline.eightWeekTotalGfs > 0 ? formatCurrency(baseline.eightWeekTotalGfs) : '--'
            )}
          </div>
        </Col>
        <Col span={12}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            8th Week GFS %
          </Text>
          <div style={{ fontWeight: 500 }}>
            {renderValue(eightWeekPercent !== null ? formatPercent(eightWeekPercent) : '--')}
          </div>
        </Col>
      </Row>
    </Card>
  );
};

